/**
 * UserMenu -- Avatar trigger with dropdown showing user info and sign-out.
 *
 * Reads user from useAuthStore. Click toggles a popover anchored bottom-right.
 * ESC and click-outside close the dropdown. No new dependencies introduced.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useIntl } from 'react-intl';
import { Key, SignOut, UserCircle } from '@phosphor-icons/react';
import { useAuthStore } from '@/stores/useAuthStore';
import { authService } from '@/services/authService';
import { Avatar } from '@/components/ui/Avatar';
import { cn } from '@/utils/cn';

// ---------------------------------------------------------------------------
// Role badge mapping
// ---------------------------------------------------------------------------

const ROLE_I18N_MAP: Record<string, string> = {
  admin: 'admin.users.role.admin',
  maintainer: 'admin.users.role.maintainer',
  member: 'admin.users.role.member',
};

const ROLE_BADGE_STYLES: Record<string, string> = {
  admin: 'bg-error-bg text-error-text',
  maintainer: 'bg-warning-bg text-warning-text',
  member: 'bg-info-bg text-info-text',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function UserMenu() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const intl = useIntl();

  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // ---- Click-outside handler ----
  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    },
    [],
  );

  // ---- ESC handler ----
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    },
    [],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleClickOutside, handleKeyDown]);

  // ---- Sign-out handler ----
  const handleSignOut = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      // best-effort -- proceed regardless
    }
    useAuthStore.getState().clearAuth();
    navigate('/login');
  }, [navigate]);

  if (!user) return null;

  const roleKey = ROLE_I18N_MAP[user.role] ?? 'admin.users.role.member';
  const roleLabel = intl.formatMessage({ id: roleKey });
  const signOutLabel = intl.formatMessage({
    id: 'user.menu.signOut',
    defaultMessage: 'Sair',
  });
  const accountLabel = intl.formatMessage({ id: 'user.menu.account' });
  const apiKeyLabel = intl.formatMessage({ id: 'user.menu.apiKey' });

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={user.display_name}
        className={cn(
          'rounded-full transition-opacity hover:opacity-80',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus',
          'cursor-pointer',
        )}
      >
        <Avatar
          src={user.avatar_url}
          name={user.display_name}
          size="sm"
        />
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          className={cn(
            'absolute right-0 top-full mt-2 z-50',
            'w-64 rounded-lg border border-border-default bg-bg-secondary shadow-lg',
            'py-2',
          )}
        >
          {/* User info */}
          <div className="px-3 py-2">
            <p className="text-sm font-semibold text-text-primary truncate">
              {user.display_name}
            </p>
            <p className="text-xs text-text-tertiary truncate mt-0.5">
              {user.email}
            </p>
            <span
              className={cn(
                'inline-block mt-1.5 px-2 py-0.5 text-[11px] font-medium rounded-full',
                ROLE_BADGE_STYLES[user.role] ?? ROLE_BADGE_STYLES.member,
              )}
            >
              {roleLabel}
            </span>
          </div>

          {/* Divider */}
          <div className="my-1 border-t border-border-default" />

          {/* Account (FEAT-033) */}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate('/settings');
            }}
            className={cn(
              'flex w-full items-center gap-2 px-3 py-2 text-sm text-text-secondary',
              'hover:bg-bg-hover hover:text-text-primary',
              'transition-colors cursor-pointer',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus',
            )}
          >
            <UserCircle size={16} weight="regular" />
            {accountLabel}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate('/settings?tab=api-key');
            }}
            className={cn(
              'flex w-full items-center gap-2 px-3 py-2 text-sm text-text-secondary',
              'hover:bg-bg-hover hover:text-text-primary',
              'transition-colors cursor-pointer',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus',
            )}
          >
            <Key size={16} weight="regular" />
            {apiKeyLabel}
          </button>

          {/* Divider */}
          <div className="my-1 border-t border-border-default" />

          {/* Sign out */}
          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            className={cn(
              'flex w-full items-center gap-2 px-3 py-2 text-sm text-text-secondary',
              'hover:bg-bg-hover hover:text-text-primary',
              'transition-colors cursor-pointer',
            )}
          >
            <SignOut size={16} weight="regular" />
            {signOutLabel}
          </button>
        </div>
      )}
    </div>
  );
}
